/**
 * Institution Billing — approved Version 1.0 commercial model.
 *
 * Shows current plan / quota state and offers Starter and Growth as
 * self-serve annual plans (US $3,600 / US $18,000). Enterprise renders
 * a "Contact sales" card. Overage price ($0.05 / verification) is
 * disclosed on every plan card.
 *
 * Backend: /api/billing/catalog/plans, /api/billing/institution/{id}/state,
 * /api/billing/stripe/checkout, /api/billing/stripe/portal
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const STATUS_COLORS = {
  active: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  past_due: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  canceled: "bg-rose-500/20 text-rose-300 border-rose-500/40",
  inactive: "bg-white/10 text-white/60 border-white/20",
};

export default function Billing() {
  const { user } = useAuth();
  const instId = user?.institution_id;
  const [plans, setPlans] = useState([]);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [starting, setStarting] = useState(null);

  useEffect(() => {
    const calls = [api.get(`/billing/catalog/plans`)];
    if (instId) calls.push(api.get(`/billing/institution/${instId}/state`));
    Promise.allSettled(calls).then((res) => {
      const [p, s] = res;
      if (p.status === "fulfilled") setPlans(p.value.data.plans || []);
      if (s && s.status === "fulfilled") setState(s.value.data);
      setLoading(false);
    }).catch((e) => {
      setErr(e.message);
      setLoading(false);
    });
  }, [instId]);

  const startCheckout = async (planId) => {
    if (!instId) return;
    setStarting(planId);
    try {
      const { data } = await api.post(`/billing/stripe/checkout`, {
        plan_id: planId,
        vlk_inst_id: instId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail?.code || e.message);
      setStarting(null);
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post(`/billing/stripe/portal`, {
        vlk_inst_id: instId,
        return_url: window.location.href,
      });
      window.location.href = data.portal_url;
    } catch (e) {
      setErr(e?.response?.data?.detail?.code || e.message);
    }
  };

  if (loading) return <div className="text-white/60" data-testid="billing-loading">Loading billing…</div>;

  const currentPlan = state?.billing?.plan;
  const billingStatus = state?.billing?.status || "inactive";
  const quota = state?.quota || {};
  const used = Number(quota.used_this_period || 0);
  const included = Number(quota.included || 0);
  const overage = Number(quota.overage_this_period || 0);
  const pct = included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 0;

  return (
    <div className="space-y-8" data-testid="billing-page">
      <header>
        <h1 className="text-2xl font-semibold text-white" data-testid="billing-heading">Billing & Plans</h1>
        <p className="text-white/60 text-sm mt-1">
          Version 1.0 approved commercial model. All institutional plans are annual only.
        </p>
      </header>

      {err && (
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-rose-200 text-sm" data-testid="billing-error">
          {String(err)}
        </div>
      )}

      {/* Current plan / quota */}
      <section
        className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
        data-testid="billing-current-plan"
      >
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-white/50 mb-1">Current plan</div>
            <div className="flex items-center gap-3">
              <div className="text-xl font-semibold text-white" data-testid="billing-plan-name">
                {currentPlan ? planLabel(currentPlan) : "No active plan"}
              </div>
              <Badge className={`border ${STATUS_COLORS[billingStatus] || STATUS_COLORS.inactive}`} data-testid="billing-status">
                {billingStatus}
              </Badge>
            </div>
          </div>
          {currentPlan && (
            <Button variant="outline" onClick={openPortal} data-testid="billing-portal-btn">
              Manage in Stripe
            </Button>
          )}
        </div>

        {currentPlan && (
          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-2">
              Verifications this period
            </div>
            <div className="flex items-baseline gap-3">
              <div className="text-3xl font-semibold text-white" data-testid="billing-used">
                {used.toLocaleString()}
              </div>
              <div className="text-white/50 text-sm">
                / {included.toLocaleString()} included
              </div>
            </div>
            <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
              <div
                className={`h-full ${pct >= 90 ? "bg-amber-400" : "bg-emerald-400"}`}
                style={{ width: `${pct}%` }}
                data-testid="billing-quota-bar"
              />
            </div>
            {overage > 0 && (
              <div className="mt-2 text-amber-300 text-sm" data-testid="billing-overage">
                {overage.toLocaleString()} overage calls · ${(overage * 0.05).toFixed(2)} billed this period
              </div>
            )}
          </div>
        )}
      </section>

      {/* Plan cards */}
      <section>
        <h2 className="text-base text-white mb-4 uppercase tracking-widest text-xs text-white/50">Available plans</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {plans.map((p) => (
            <PlanCard
              key={p.plan_id}
              plan={p}
              isCurrent={p.plan_id === currentPlan}
              onSelect={() => startCheckout(p.plan_id)}
              starting={starting === p.plan_id}
            />
          ))}
        </div>
      </section>

      <footer className="text-xs text-white/40 leading-relaxed" data-testid="billing-footer">
        Prices approved 2026-02-19. Overage above included quota billed at $0.05 / verification
        (Starter and Growth) or volume-tiered $0.02–$0.04 (Enterprise). Refund policy: pro-rata
        annual refund within 14 days of first payment.
      </footer>
    </div>
  );
}

function planLabel(planId) {
  return {
    starter: "Institution Starter",
    growth: "Institution Growth",
    enterprise: "Institution Enterprise",
  }[planId] || planId;
}

function PlanCard({ plan, isCurrent, onSelect, starting }) {
  const isEnterprise = plan.plan_id === "enterprise";
  return (
    <div
      className={`rounded-2xl border p-6 flex flex-col gap-4 transition-all ${
        isCurrent
          ? "border-emerald-500/50 bg-emerald-500/[0.06]"
          : "border-white/10 bg-white/[0.03] hover:border-white/30"
      }`}
      data-testid={`billing-plan-card-${plan.plan_id}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-white font-semibold text-lg" data-testid={`billing-plan-name-${plan.plan_id}`}>
            {plan.display_name}
          </div>
          {isCurrent && (
            <Badge className="mt-1 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" data-testid={`billing-plan-current-${plan.plan_id}`}>
              Current
            </Badge>
          )}
        </div>
      </div>

      <div>
        <div className="text-2xl text-white font-semibold" data-testid={`billing-plan-price-${plan.plan_id}`}>
          {plan.price_display}
        </div>
        <div className="text-xs text-white/50 mt-1">
          {plan.included_verifications_per_month
            ? `${plan.included_verifications_per_month.toLocaleString()} verifications / month included`
            : "Custom included volume"}
        </div>
        <div className="text-xs text-white/50">
          Overage: {plan.overage_display}
        </div>
      </div>

      <ul className="text-sm text-white/70 space-y-1 mt-2 list-disc list-inside">
        {(plan.features || []).map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>

      <div className="mt-auto pt-2">
        {isEnterprise ? (
          <Button
            variant="outline"
            className="w-full"
            data-testid={`billing-plan-contact-${plan.plan_id}`}
            onClick={() => window.location.href = "mailto:sales@veralok.io?subject=VeraLok%20Enterprise%20plan%20enquiry"}
          >
            Contact sales
          </Button>
        ) : (
          <Button
            className="w-full"
            disabled={isCurrent || starting}
            onClick={onSelect}
            data-testid={`billing-plan-select-${plan.plan_id}`}
          >
            {isCurrent ? "Current plan" : starting ? "Redirecting…" : `Subscribe to ${plan.display_name}`}
          </Button>
        )}
      </div>
    </div>
  );
}
